/**
 * Named aliases over the generated contract types.
 *
 * `schema.d.ts` is produced by `npm run gen:api` from
 * `backend/api/openapi.yaml` and is never hand-edited. Every type the app uses
 * is re-exported from here, so a contract change surfaces as a compile error in
 * one file rather than as a runtime surprise in twenty.
 */
import type { components } from './schema';

type S = components['schemas'];

// -- identity ---------------------------------------------------------------
export type UserIdentity = S['UserIdentity'];
export type Session = S['Session'];
export type Role = S['Role'];

// -- vocabulary -------------------------------------------------------------
export type ChatStatus = S['ChatStatus'];
export type ClientChatStatus = S['ClientChatStatus'];
export type ComposerReasonCode = S['ComposerReasonCode'];
export type RunPhase = S['RunPhase'];
export type RunStatus = S['RunStatus'];
export type ActorKind = S['ActorKind'];
export type MessageAuthorKind = S['MessageAuthorKind'];
export type DeployStatus = S['DeployStatus'];
export type DeployEnvironment = S['DeployEnvironment'];

// -- chat aggregates --------------------------------------------------------
export type ClientProjectSummary = S['ClientProjectSummary'];
export type ClientChat = S['ClientChat'];
export type ChatMessage = S['ChatMessage'];
export type MessageBlock = S['MessageBlock'];
export type TextBlock = S['TextBlock'];
export type AskBlock = S['AskBlock'];
export type NoticeBlock = S['NoticeBlock'];
export type ActionBlock = S['ActionBlock'];
export type Attachment = S['Attachment'];
export type Preview = S['Preview'];
export type ComposerState = S['ComposerState'];
export type RunSummary = NonNullable<S['RunSummary']>;
export type RunRepo = S['RunRepo'];
export type Deploy = NonNullable<S['Deploy']>;

// -- stream -----------------------------------------------------------------
export type SseClientEvent = S['SseClientEvent'];
export type SseClientEventKind = S['SseClientEventKind'];
export type SseStreamOpen = S['SseStreamOpen'];
export type SseStreamReset = S['SseStreamReset'];
export type SseStatusEvent = S['SseStatus'];
export type SseRunStarted = S['SseRunStarted'];
export type SsePhase = S['SsePhase'];
export type SseMessageEvent = S['SseMessage'];
export type SseTextDelta = S['SseTextDelta'];
export type SseAsk = S['SseAsk'];
export type SseAskResolved = S['SseAskResolved'];
export type SseDeploy = S['SseDeploy'];
export type SseErrorCode = S['SseErrorCode'];
export type SseRunFinished = S['SseRunFinished'];
export type SseRunFailed = S['SseRunFailed'];

// -- requests ---------------------------------------------------------------
export type PostMessageRequest = S['PostMessageRequest'];
export type AnswerAskRequest = S['AnswerAskRequest'];
export type MarkDoneRequest = S['MarkDoneRequest'];

/**
 * The exhaustive set of stream kinds this dashboard knows how to handle.
 *
 * It mirrors `SseClientEventKind` from the contract. It exists as a runtime
 * value because a type cannot check an incoming frame: anything not in this set
 * is routed to <UnknownTurn>, which is a leak detector, not a fallback. New
 * kinds default to internal on the server, so an unrecognised kind here means
 * either a contract drift or a scrub-projection bug — both worth shouting about.
 */
export const CLIENT_EVENT_KINDS = [
  'stream_open',
  'stream_reset',
  'status',
  'run_started',
  'phase',
  'message',
  'text_delta',
  'ask',
  'ask_resolved',
  'deploy',
  'error_code',
  'run_finished',
  'run_failed',
] as const satisfies readonly SseClientEventKind[];

const CLIENT_EVENT_KIND_SET: ReadonlySet<string> = new Set(CLIENT_EVENT_KINDS);

export function isKnownClientEventKind(kind: string): kind is SseClientEventKind {
  return CLIENT_EVENT_KIND_SET.has(kind);
}

/**
 * Block types a client surface may render. `action` is deliberately absent: it
 * is an expert-only block, and its arrival on this surface is an I7 leak that
 * must be visible rather than quietly drawn.
 */
export const CLIENT_BLOCK_TYPES = ['text', 'ask', 'notice'] as const;
export type ClientBlockType = (typeof CLIENT_BLOCK_TYPES)[number];

const CLIENT_BLOCK_TYPE_SET: ReadonlySet<string> = new Set(CLIENT_BLOCK_TYPES);

export function isClientBlockType(type: string): type is ClientBlockType {
  return CLIENT_BLOCK_TYPE_SET.has(type);
}

/** Author kinds a client surface may render. `expert` is never one of them. */
export const CLIENT_AUTHOR_KINDS = ['client', 'agent', 'system'] as const;
export type ClientAuthorKind = (typeof CLIENT_AUTHOR_KINDS)[number];

const CLIENT_AUTHOR_KIND_SET: ReadonlySet<string> = new Set(CLIENT_AUTHOR_KINDS);

export function isClientAuthorKind(kind: string): kind is ClientAuthorKind {
  return CLIENT_AUTHOR_KIND_SET.has(kind);
}
